/**
 * useTawkTo - Professional tawk.to Integration Hook
 *
 * Features:
 * - Delayed loading (5 seconds) for better page performance & SEO
 * - Automatic user identity passing (name, email, custom attributes)
 * - Show/Hide widget programmatically
 * - Smart triggers support
 * - Prevents duplicate script injection
 */

import { useEffect, useRef } from 'react';

const TAWK_PROPERTY_ID = '699ce597d737701c38a643f8';
const TAWK_WIDGET_ID = '1ji6e1mau';
const TAWK_SRC = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${TAWK_WIDGET_ID}`;

/**
 * @param {object} options
 * @param {string}  options.name       - Visitor name (auto-passed to agent)
 * @param {string}  options.email      - Visitor email
 * @param {object}  options.attributes - Extra custom attributes (e.g. { tableNumber, restaurantName })
 * @param {number}  options.delay      - Seconds before loading widget (default: 5)
 * @param {boolean} options.hide       - Start with widget hidden
 */
const useTawkTo = ({
    name = null,
    email = null,
    attributes = {},
    delay = 5,
    hide = false,
} = {}) => {
    const loaded = useRef(false);

    useEffect(() => {
        // Prevent duplicate injection — check both the ref and actual DOM
        if (loaded.current) return;
        if (document.querySelector(`script[src="${TAWK_SRC}"]`)) {
            loaded.current = true;
            return;
        }

        const timer = setTimeout(() => {
            // Prevent race condition: check again just before injecting
            if (document.querySelector(`script[src="${TAWK_SRC}"]`)) {
                loaded.current = true;
                return;
            }

            // --- Setup Tawk API pre-load config ---
            window.Tawk_API = window.Tawk_API || {};
            window.Tawk_LoadStart = new Date();

            // Set visitor identity when widget loads
            window.Tawk_API.onLoad = function () {
                if (name || email || Object.keys(attributes).length > 0) {
                    window.Tawk_API.setAttributes(
                        {
                            name: name || 'زائر',
                            email: email || '',
                            ...attributes,
                        },
                        function (error) {
                            if (error) console.warn('[tawk.to] setAttributes error:', error);
                        }
                    );
                }

                if (hide) {
                    window.Tawk_API.hideWidget();
                }
            };

            // Inject the script
            const s1 = document.createElement('script');
            const s0 = document.getElementsByTagName('script')[0];
            s1.async = true;
            s1.src = TAWK_SRC;
            s1.charset = 'UTF-8';
            s1.setAttribute('crossorigin', '*');
            s0.parentNode.insertBefore(s1, s0);

            loaded.current = true;
        }, delay * 1000);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // === Control API helpers ===

    const showWidget = () => {
        if (window.Tawk_API?.showWidget) window.Tawk_API.showWidget();
    };

    const hideWidget = () => {
        if (window.Tawk_API?.hideWidget) window.Tawk_API.hideWidget();
    };

    const openChat = () => {
        if (window.Tawk_API?.maximize) window.Tawk_API.maximize();
    };

    const sendEvent = (eventName) => {
        if (window.Tawk_API?.addEvent) {
            window.Tawk_API.addEvent(eventName, {}, function (error) {
                if (error) console.warn('[tawk.to] addEvent error:', error);
            });
        }
    };

    return { showWidget, hideWidget, openChat, sendEvent };
};

export default useTawkTo;
